// Simple metadata transformation utilities for Search filters
import { CarManufacturerDto, VehicleTagModel, VehicleTypeModel } from "api"
import { mapCarTypeToImage, TypeImage } from "./carTypeImageMapping"

// Remove duplicates by code
function removeDuplicatesByCode<T extends { [key: string]: any }>(
  items: T[],
  codeKey: string,
): T[] {
  if (!items) return []
  const seen = new Set()
  return items.filter((item) => {
    const code = item[codeKey]
    if (seen.has(code)) return false
    seen.add(code)
    return true
  })
}

// Transform tags for CarFeaturesSection
export function transformCarFeatures(tags: VehicleTagModel[]) {
  const uniqueTags = removeDuplicatesByCode(tags, "vehicleTagForWebsiteCode")
  return uniqueTags.map((tag) => ({
    id: tag.vehicleTagForWebsiteCode.toString(),
    label: tag.vehicleTagForWebsiteDescription,
  }))
}

// Vehicle type to icon mapping
const vehicleTypeIconMap: Record<number, string> = {
  1: "/icons/car-suv.svg", // "ג'יפ (פנאי שטח- גדול)"
  2: "/icons/car-suv.svg", // "ג'יפונים (פנאי שטח- בינוני)"
  4: "/icons/car-big-family.svg", // "מינוואנים"
  6: "/icons/car-manager.svg", // "מנהלים"
  // Add more mappings as needed
}

// Transform types for CarTypeSection
export function transformCarTypes(
  types: VehicleTypeModel[],
  typeImages?: TypeImage[],
) {
  const uniqueTypes = removeDuplicatesByCode(types, "vehicleTypeForWebsiteCode")
  return uniqueTypes.map((type) => {
    // If typeImages are provided, try to map to Umbraco images
    if (typeImages && typeImages.length > 0) {
      const imageData = mapCarTypeToImage(
        type.vehicleTypeForWebsiteDescription,
        typeImages,
      )
      if (imageData) {
        return {
          id: type.vehicleTypeForWebsiteCode.toString(),
          label: type.vehicleTypeForWebsiteDescription,
          icon: imageData.imageNormal, // Use imageNormal as default icon
          imageNormal: imageData.imageNormal,
          imageOn: imageData.imageOn,
        }
      }
    }

    // Fallback to hardcoded icon paths for backward compatibility
    return {
      id: type.vehicleTypeForWebsiteCode.toString(),
      label: type.vehicleTypeForWebsiteDescription,
      icon:
        vehicleTypeIconMap[type.vehicleTypeForWebsiteCode] ||
        "/icons/car-mini.svg",
    }
  })
}

// Transform manufacturers for ManufacturerModelSection
export function transformManufacturers(manufacturers: CarManufacturerDto[]) {
  const uniqueManufacturers = removeDuplicatesByCode(manufacturers, "code")
  return uniqueManufacturers.map((manufacturer) => ({
    value: manufacturer.code.toString(),
    label: manufacturer.name,
    models: manufacturer.models.map((model) => ({
      value: model.modelCode.toString(),
      label: model.name,
    })),
  }))
}
